import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as svc from './catalogos-admin-service';
import { catalogosAdminKeys } from './keys';
import type {
  CreateCatalogCategoryDto,
  CreateCatalogoDto,
  QueryCatalogosDto,
  UpdateCatalogCategoryDto,
  UpdateCatalogoDto,
} from './catalogos-admin-service';

export function useCatalogosAdminList(params: QueryCatalogosDto) {
  return useQuery({
    // SAFETY: QueryCatalogosDto is a serializable record used only as a cache key.
    queryKey: catalogosAdminKeys.list(params as unknown as Record<string, unknown>),

    queryFn: () => svc.getCatalogos(params),
    staleTime: 30_000,
  });
}

export function useCatalogoAdmin(id: number) {
  return useQuery({
    queryKey: catalogosAdminKeys.detail(id),
    queryFn: () => svc.getCatalogo(id),
    enabled: id > 0,
    staleTime: 30_000,
  });
}

export function useCreateCatalogoAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateCatalogoDto) => svc.createCatalogo(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogosAdminKeys.lists() });
      qc.invalidateQueries({ queryKey: catalogosAdminKeys.categorias() });
    },
  });
}

export function useUpdateCatalogoAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: UpdateCatalogoDto }) =>
      svc.updateCatalogo(id, dto),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: catalogosAdminKeys.detail(variables.id) });
      qc.invalidateQueries({ queryKey: catalogosAdminKeys.lists() });
      qc.invalidateQueries({ queryKey: catalogosAdminKeys.categorias() });
    },
  });
}

export function useDeleteCatalogoAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => svc.deleteCatalogo(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogosAdminKeys.lists() });
      qc.invalidateQueries({ queryKey: catalogosAdminKeys.categorias() });
    },
  });
}

export function useCategoriasList() {
  return useQuery({
    queryKey: catalogosAdminKeys.categorias(),
    queryFn: () => svc.getCategorias(),
    staleTime: 300_000,
  });
}

export function useCategoriaAdmin(code: string | undefined) {
  return useQuery({
    queryKey: catalogosAdminKeys.categoria(code ?? ''),
    queryFn: () => {
      if (!code) throw new Error('A category code is required');
      return svc.getCategoria(code);
    },
    enabled: Boolean(code),
    staleTime: 30_000,
  });
}

export function useCreateCategoriaAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateCatalogCategoryDto) => svc.createCategoria(dto),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogosAdminKeys.categorias() });
    },
  });
}

export function useUpdateCategoriaAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, dto }: { code: string; dto: UpdateCatalogCategoryDto }) =>
      svc.updateCategoria(code, dto),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: catalogosAdminKeys.categorias() });
      qc.invalidateQueries({ queryKey: catalogosAdminKeys.categoria(variables.code) });
    },
  });
}

export function useDeleteCategoriaAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => svc.deleteCategoria(code),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: catalogosAdminKeys.categorias() });
    },
  });
}
